import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { IconCheck, IconX } from "@tabler/icons-react";
import { cn } from "@/utils/cn";
import { TeeButton } from "@/components/ui/tee-button";
import { TeeInput } from "@/components/ui/tee-input";
import { TeeTextArea } from "@/components/ui/tee-textarea";
import { TeeLink } from "@/components/ui/tee-link";
import { showErrorToast } from "@/utils/toast.utils";
import { feedbackService } from "@/services/feedbackService";
import {
  FEEDBACK_CATEGORIES,
  FeedbackCategory,
  FeedbackResponse,
} from "@/types/feedback";
import { FeedbackScreenshotZone } from "./FeedbackScreenshotZone";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TITLE_MAX = 200;

/**
 * Feedback form styled per the ENG-616 mock: a forest-green sheet with
 * tap-select area cards, title, description, screenshot drop zone and a gold
 * submit CTA. Submits straight to the API, which files a Linear issue in the
 * team that owns the selected area.
 */
export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [category, setCategory] = useState<FeedbackCategory>("box_os");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<FeedbackResponse | null>(null);

  // Reset to a blank form every time the sheet opens.
  useEffect(() => {
    if (!isOpen) return;
    setCategory("box_os");
    setTitle("");
    setDescription("");
    setScreenshots([]);
    setIsSubmitting(false);
    setResult(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const canSubmit =
    title.trim().length >= 3 && description.trim().length >= 3 && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const response = await feedbackService.submit({
        category,
        title: title.trim(),
        description: description.trim(),
        pageUrl: `${window.location.origin}${location.pathname}${location.search}`,
        screenshots,
      });
      setResult(response);
    } catch (error) {
      const message = (
        error as { response?: { data?: { message?: string | string[] } } }
      )?.response?.data?.message;
      showErrorToast(
        Array.isArray(message)
          ? message.join(", ")
          : message || t("feedback.error_submit"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelClass =
    "text-[20px] font-bold leading-tight text-[var(--text-on-forest)]";

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{
          background: "rgba(12, 61, 45, 0.30)",
          backdropFilter: "blur(3px)",
        }}
        onClick={isSubmitting ? undefined : onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
        className={cn(
          "fixed z-50 flex flex-col overflow-visible bg-[var(--color-forest)] text-[var(--text-on-forest)]",
          // Mobile — bottom sheet
          "inset-x-0 bottom-0 max-h-[92vh] rounded-t-[16px]",
          // Desktop — centred card
          "md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:w-[640px] md:max-w-[calc(100vw-32px)] md:max-h-[calc(100vh-48px)]",
          "md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[16px]",
          "shadow-[0_18px_48px_rgba(8,42,31,0.45)]",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label={t("common.close")}
          disabled={isSubmitting}
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--background-card)] text-[var(--color-forest)] shadow-[0_2px_8px_rgba(0,0,0,0.25)] hover:bg-[var(--background-card-hover)] md:-right-3 md:-top-3"
        >
          <IconX size={20} />
        </button>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] pt-6 md:px-8 md:pb-8 md:pt-8">
          {result ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-gold-500)] text-[var(--color-forest)]">
                <IconCheck size={28} />
              </span>
              <h2 id="feedback-modal-title" className="text-[24px] font-bold">
                {t("feedback.success_heading")}
              </h2>
              <p className="text-[16px] text-[var(--text-on-forest-muted)]">
                {t("feedback.success_body", { identifier: result.identifier })}
              </p>
              <TeeLink
                href={result.url}
                external
                variant="secondary"
                size="m"
                className="text-[var(--text-on-forest)] underline"
              >
                {t("feedback.success_view_link", {
                  identifier: result.identifier,
                })}
              </TeeLink>
              <TeeButton
                variant="primary"
                size="l"
                text={t("common.done")}
                onClick={onClose}
                className="mt-2 w-full bg-[var(--color-gold-500)] text-[var(--color-forest)] hover:bg-[var(--color-gold-500)] hover:brightness-95"
              />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <fieldset className="flex flex-col gap-3">
                <legend
                  id="feedback-modal-title"
                  className={cn(labelClass, "mb-3 text-[24px]")}
                >
                  {t("feedback.area_question")}
                </legend>
                <div
                  role="radiogroup"
                  className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                >
                  {FEEDBACK_CATEGORIES.map((option) => {
                    const selected = option === category;
                    return (
                      <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={isSubmitting}
                        onClick={() => setCategory(option)}
                        className={cn(
                          "flex items-center gap-3 rounded-[10px] px-4 py-4 text-left text-[18px] font-semibold transition-colors",
                          selected
                            ? "bg-[var(--background-card)] text-[var(--text-content-title)]"
                            : "bg-[var(--background-card-elevated)] text-[var(--text-content-title)] hover:bg-[var(--background-card)]",
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={cn(
                            "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 border-[var(--color-forest)]",
                            selected && "bg-[var(--color-forest)]",
                          )}
                        >
                          {selected && (
                            <span className="h-2 w-2 rounded-full bg-[var(--background-card)]" />
                          )}
                        </span>
                        {t(`feedback.category_${option}`)}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="flex flex-col gap-2">
                <label htmlFor="feedback-title" className={labelClass}>
                  {t("feedback.title_label")}
                </label>
                <TeeInput
                  id="feedback-title"
                  value={title}
                  maxLength={TITLE_MAX}
                  disabled={isSubmitting}
                  placeholder={t("feedback.title_placeholder")}
                  onChange={(e) => setTitle(e.target.value)}
                  inputSize="m"
                  className="h-14 rounded-[10px] px-4 text-[16px]"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="feedback-description" className={labelClass}>
                  {t("feedback.description_label")}
                </label>
                <TeeTextArea
                  id="feedback-description"
                  value={description}
                  rows={6}
                  disabled={isSubmitting}
                  showLabel={false}
                  placeholder={t("feedback.description_placeholder")}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[180px] resize-y rounded-[10px] px-4 py-3 text-[16px]"
                />
              </div>

              <FeedbackScreenshotZone
                files={screenshots}
                onChange={setScreenshots}
                onError={showErrorToast}
                disabled={isSubmitting}
              />

              <div className="flex flex-col items-center gap-3">
                <TeeButton
                  type="submit"
                  variant="primary"
                  size="xl"
                  disabled={!canSubmit}
                  text={
                    isSubmitting
                      ? t("feedback.submitting")
                      : t("feedback.submit")
                  }
                  className="w-full bg-[var(--color-gold-500)] text-[var(--color-forest)] text-[20px] hover:bg-[var(--color-gold-500)] hover:brightness-95 disabled:bg-[var(--color-gold-500)] disabled:text-[var(--color-forest)] disabled:opacity-60"
                />
                <p className="text-[16px] text-[var(--text-on-forest-muted)]">
                  {t("feedback.footnote")}
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedbackModal;
